// PtsBySeasonChart — pure CSS mini bar chart showing PTS/g per season.
//
// DESIGN: "UI design refinement/HoopRoster Profile Redesign.dc.html" section 1b
// lines 597-606 (dark theme) — repurposed for 2a light rail in the merged direction.
// Pure CSS bars (hoop-accent fill, most recent emphasized, best season green).
// Renders in the left rail below IdentityCard. Skip when <2 seasons of data.

import { type CareerStatsRow } from './CareerStatsTable'

interface Props {
  careerStats: CareerStatsRow[]
}

export default function PtsBySeasonChart({ careerStats }: Props) {
  // Skip render when <2 seasons
  if (careerStats.length < 2) return null

  // Build chart data: season, PTS/g, games played (for validation)
  const data = careerStats
    .filter(r => r.points_pg != null && r.games_played != null)
    .map(r => ({
      season: r.season,
      pts: r.points_pg!,
      gp: r.games_played!,
    }))
    .slice(0, 4) // max 4 bars

  if (data.length < 2) return null

  // Find max PTS/g for scaling bar heights
  const maxPts = Math.max(...data.map(d => d.pts))

  // Find best season (highest PTS/g, ties break to most games)
  let bestIdx = 0
  let bestPts = data[0].pts
  let bestGp = data[0].gp
  data.forEach((d, i) => {
    if (d.pts > bestPts || (d.pts === bestPts && d.gp > bestGp)) {
      bestIdx = i
      bestPts = d.pts
      bestGp = d.gp
    }
  })

  // Most recent season is index 0 (career_stats is DESC sorted)
  const mostRecentIdx = 0

  return (
    <section className="bg-hoop-surface border border-hoop-border rounded-2xl p-4">
      <h2 className="text-[11px] font-semibold uppercase tracking-wider text-hoop-text-3 mb-3.5">
        PTS by season
      </h2>
      {/* UX Issue 2 (Kaillum 07-16): no fixed height — value(≈16) + bar(≤56)
        * + label(≈14) + gaps overflowed the old h-[78px] box, jamming the
        * season labels against the card edge. Auto height lets the card's
        * p-4 breathe below the labels. */}
      <div className="flex items-end gap-3">
        {data.map((d, i) => {
          // Height is proportional to pts/maxPts, scaled to 56px max
          const pct = (d.pts / maxPts) * 100
          const height = Math.max(12, (pct / 100) * 56) // min 12px so tiny bars are visible

          const isBest = i === bestIdx
          const isMostRecent = i === mostRecentIdx

          const barColor = isBest
            ? 'bg-hoop-green'
            : isMostRecent
            ? 'bg-hoop-accent'
            : 'bg-hoop-text-3/40'

          return (
            <div key={d.season} className="flex-1 flex flex-col items-center gap-1.5">
              <span className={`font-stat text-[11px] ${isBest ? 'text-hoop-green font-semibold' : 'text-hoop-text-2'}`}>
                {d.pts.toFixed(1)}
              </span>
              <div
                className={`w-full rounded-t ${barColor}`}
                style={{ height: `${height}px` }}
              />
              <span className="text-[10px] text-hoop-text-3">
                {shortSeason(d.season)}
              </span>
            </div>
          )
        })}
      </div>
    </section>
  )
}

function shortSeason(s: string): string {
  // "2025" → "'25", "2023-24" → "'23"
  const m = s.match(/(\d{4})/)
  if (!m) return s
  return `'${m[1].slice(2)}`
}
